<template>
  <div v-if="canUse" class="ai-assistant">
    <!-- 悬浮球：面板打开时隐藏，关闭后再显示（避免与面板重叠）
         图标语义：对话气泡=可提问，星光=AI 智能；标题说清「是什么 + 边界」 -->
    <div
      v-show="!open"
      class="ball"
      title="智慧运维助手 · 只读问答，不修改任何数据"
      aria-label="打开智慧运维助手"
      @click="toggle"
    >
      <svg
        class="ball-ico"
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        stroke-width="1.9"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <!-- 对话气泡（带尾巴） -->
        <path d="M9.2 8.2h13.6a3.2 3.2 0 0 1 3.2 3.2v7.4a3.2 3.2 0 0 1-3.2 3.2h-6.6L10 25.6V22h-.8a3.2 3.2 0 0 1-3.2-3.2v-7.4a3.2 3.2 0 0 1 3.2-3.2Z" />
        <!-- 内部星光：AI 智能 -->
        <path
          d="M16.2 11.1l1.4 2.7 2.7 1.4-2.7 1.4-1.4 2.7-1.4-2.7-2.7-1.4 2.7-1.4 1.4-2.7Z"
          fill="currentColor"
          stroke="none"
        />
      </svg>
      <span class="dot"></span>
    </div>

    <!-- 对话面板 -->
    <div v-show="open" class="panel">
      <!-- 头部 -->
      <div class="phead">
        <div class="av">AI</div>
        <div class="t">
          <b>智慧运维助手<span class="live"></span></b>
          <s>只读问答 · 不会修改任何数据</s>
        </div>
        <div class="acts">
          <span class="iconbtn" title="会话历史" @click="toggleHistory">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 8v4l3 3"/><circle cx="12" cy="12" r="9"/></svg>
          </span>
          <span class="iconbtn" title="新会话" @click="newSession">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
          </span>
          <span class="iconbtn" title="关闭" @click="open = false">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </span>
        </div>
      </div>

      <!-- 会话历史侧栏 -->
      <div v-if="histOpen" class="hist">
        <div class="hh">
          最近会话
          <a v-if="sessions.length" @click="clearSessions">清空</a>
        </div>
        <div class="hs">
          <div v-if="histLoading" class="hist-empty">加载中…</div>
          <div v-else-if="!sessions.length" class="hist-empty">暂无历史会话</div>
          <div
            v-for="s in sessions"
            :key="s.id"
            class="hitem"
            :class="{ on: s.id === sessionId }"
            @click="loadSession(s.id)"
          >
            <div class="ht">
              <b>{{ s.title }}</b>
              <s>{{ fmtTime(s.updateTime) }} · {{ s.msgCount }} 轮对话</s>
            </div>
            <span class="htag">{{ topicTag(s.topicTag) }}</span>
          </div>
        </div>
      </div>

      <!-- 消息区 -->
      <div ref="list" class="plist">
        <!-- 空态引导 -->
        <div v-if="!messages.length" class="welcome">
          <div class="hello">AI</div>
          <h3>你好，我是运维助手</h3>
          <p>帮你查水质数据、解读告警、梳理今日待办、找功能入口。<br />回答均来自系统真实数据。</p>
          <div class="qs">
            <div v-for="q in quickQuestions" :key="q.b" class="q" @click="ask(q.b)">
              <div class="qi" :class="q.k">{{ q.icon }}</div>
              <div class="qt"><b>{{ q.b }}</b><s>{{ q.s }}</s></div>
            </div>
          </div>
          <div class="boundary">
            <b>能力边界</b>：助手只能查询和解读，<b>不能</b>建工单、提交巡检、修改库存等写操作，此类需求请到对应页面手工处理。
          </div>
        </div>

        <!-- 消息 -->
        <template v-for="(m, i) in messages">
          <div :key="'m' + i" class="msg" :class="m.role">
            <div class="bub" v-if="m.role === 'user'">{{ m.text }}</div>
            <template v-else>
              <!-- 工具调用条 -->
              <div v-if="m.tool && m.tool.name" class="tool" :class="{ done: m.tool.done }">
                <div class="spin"></div>
                <b>{{ m.tool.done ? '已调用 ' : '正在调用 ' }}{{ m.tool.name }} · {{ m.tool.label }}</b>
                <span v-if="m.tool.done && m.tool.ms != null" class="ms">{{ (m.tool.ms / 1000).toFixed(1) }}s</span>
              </div>
              <div class="row">
                <div class="mav">AI</div>
                <div class="bub">
                  <p v-for="(p, pi) in splitPara(m.text)" :key="'p' + pi">{{ p }}</p>
                  <span v-if="m.pending" class="caret"></span>
                  <!-- 数据卡 -->
                  <div v-for="(c, ci) in m.cards" :key="'c' + ci" class="dcard">
                    <div class="h">
                      <b>{{ c.title }}</b>
                      <span v-if="c.subtitle">{{ c.subtitle }}</span>
                    </div>
                    <table v-if="c.head && c.rows && c.rows.length">
                      <tr>
                        <th v-for="(h, hi) in c.head" :key="'h' + hi">{{ h }}</th>
                      </tr>
                      <tr v-for="(row, ri) in c.rows" :key="'r' + ri">
                        <td v-for="(cell, ci2) in row" :key="'c' + ci2" :class="cellClass(cell)">
                          {{ cellText(cell) }}
                        </td>
                      </tr>
                    </table>
                  </div>
                </div>
              </div>
              <!-- 来源 + 追问 chips -->
              <div v-if="m.sources && m.sources.length" class="cite">
                <span class="tag">数据来源</span>{{ m.sources.join(' · ') }}
              </div>
              <div v-if="m.suggestions && m.suggestions.length" class="chips">
                <b v-for="(s, si) in m.suggestions" :key="'s' + si" @click="ask(s)">{{ s }}</b>
              </div>
            </template>
          </div>
        </template>
      </div>

      <!-- 输入区 -->
      <div class="pfoot">
        <div class="qbox">
          <textarea
            v-model="input"
            class="tx"
            :class="{ ph: !input }"
            :placeholder="'问数据、告警、待办…'"
            rows="1"
            @keydown.enter.exact.prevent="send"
          ></textarea>
          <button class="send" :class="{ off: sending }" :disabled="sending" @click="send">
            {{ sending ? '发送中' : '发送' }}
          </button>
        </div>
        <div class="fnote">Enter 发送 · Shift+Enter 换行 · 回答基于实时接口数据</div>
      </div>
    </div>
  </div>
</template>

<script>
import { getApiBase } from "@/utils/request";
import { watchPerm } from "@/utils/tabs";
import { aiSessions, aiMessages, aiClearSessions } from "@/api/ai";

const QUICK_QUESTIONS = [
  { icon: "数", k: "d", b: "俞庄泵站最近 7 天氨氮趋势如何？", s: "查水质数据" },
  { icon: "警", k: "w", b: "今天有哪些设备告警？严重的有几条", s: "看告警" },
  { icon: "办", k: "t", b: "我今天有哪些待处理的任务和工单？", s: "理待办" },
  { icon: "导", k: "g", b: "巡检日志在哪里查看？", s: "找功能入口" },
];

export default {
  name: "AiAssistant",
  data() {
    return {
      // 🔴 $hasPerm 是「宁松勿紧」设计（未登录 permInfo 为空 → 一律放行 true），
      //    单靠它未登录时悬浮球会漏到登录页，必须叠加「已登录 + 不在登录页」两道前置。
      canUse:
        !!localStorage.getItem("token") &&
        !(this.$route && this.$route.path === "/login") &&
        this.$hasPerm("ai:chat"),
      open: false,
      histOpen: false,
      input: "",
      sending: false,
      sessionId: null,
      messages: [],
      sessions: [],
      histLoading: false,
      quickQuestions: QUICK_QUESTIONS,
      abortCtl: null,
    };
  },
  watch: {
    // 登出是 SPA 内 push /login（不清 token、不整页刷新）→ 路由变化时重算，避免悬浮球残留
    $route() {
      this.refreshCanUse();
    },
  },
  mounted() {
    // 权限异步拉取：permInfo 到位后复查（登录成功 emit permLoaded → 重算显示）
    watchPerm(this, () => {
      this.refreshCanUse();
    });
  },
  methods: {
    refreshCanUse() {
      const logged = !!localStorage.getItem("token");
      const onLogin = this.$route && this.$route.path === "/login";
      this.canUse = logged && !onLogin && this.$hasPerm("ai:chat");
    },
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.histOpen = false;
        this.$nextTick(() => this.scrollBottom());
      }
    },
    toggleHistory() {
      this.histOpen = !this.histOpen;
      if (this.histOpen) this.loadSessions();
    },
    // 新建会话（中断进行中的回答）
    newSession() {
      if (this.abortCtl) {
        this.abortCtl.abort();
        this.abortCtl = null;
      }
      this.sessionId = null;
      this.messages = [];
      this.sending = false;
      this.histOpen = false;
      this.input = "";
    },
    // 空态引导卡 / 追问 chip：填入问题并发送
    ask(text) {
      this.input = text;
      this.send();
    },
    async send() {
      const text = (this.input || "").trim();
      if (!text || this.sending) return;
      this.sending = true;
      this.histOpen = false;
      this.input = "";

      // user 消息
      this.messages.push({ role: "user", text });
      // assistant 占位
      this.messages.push({
        role: "assistant",
        text: "",
        cards: [],
        sources: [],
        suggestions: [],
        tool: null,
        pending: true,
      });
      this.scrollBottom();

      const base = getApiBase();
      const token = localStorage.getItem("token") || "";
      this.abortCtl = new AbortController();
      try {
        const resp = await fetch(base + "/ai/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ message: text, sessionId: this.sessionId }),
          signal: this.abortCtl.signal,
        });
        const ctype = resp.headers.get("content-type") || "";
        if (!ctype.includes("text/event-stream")) {
          // 非 SSE = 鉴权/无权限 Result JSON（HTTP 恒 200）
          const body = await resp.json().catch(() => ({}));
          const code = body.code == null ? "" : String(body.code);
          if (code === "304") this.appendLast("无 AI 助手使用权限，请联系管理员开通。");
          else if (code === "401" || code === "501") this.appendLast("登录已失效，请重新登录。");
          else this.appendLast(body.message || "请求失败，请稍后再试。");
          return;
        }
        await this.readStream(resp.body);
      } catch (e) {
        if (e.name !== "AbortError") this.appendLast("网络异常：" + e.message);
      } finally {
        this.sending = false;
        this.abortCtl = null;
        this.finishPending();
        this.scrollBottom();
      }
    },
    async readStream(body) {
      const reader = body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let done = false;
      while (!done) {
        const res = await reader.read();
        done = res.done;
        if (done) break;
        buffer += decoder.decode(res.value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          this.handleSse(chunk);
        }
      }
    },
    handleSse(chunk) {
      let event = "message";
      let data = "";
      chunk.split("\n").forEach((line) => {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      });
      if (!data) return;
      let payload;
      try {
        payload = JSON.parse(data);
      } catch (e) {
        return;
      }
      const last = this.lastAssistant();
      if (event === "delta") {
        this.appendLast(payload.text || "");
      } else if (event === "tool_start") {
        if (last) {
          last.tool = { name: payload.name || "", label: payload.label || payload.name || "", done: false, ms: null };
        }
      } else if (event === "tool_end") {
        if (last && last.tool) {
          last.tool.done = true;
          last.tool.ms = payload.ms != null ? payload.ms : null;
        }
      } else if (event === "card") {
        if (last) {
          if (Array.isArray(payload)) last.cards.push(...payload);
          else last.cards.push(payload);
        }
      } else if (event === "done") {
        if (payload && payload.sessionId) this.sessionId = payload.sessionId;
        if (last) {
          last.sources = payload.sources || [];
          last.suggestions = payload.suggestions || [];
        }
      } else if (event === "error") {
        this.appendLast(payload.message || "出错了");
      }
      this.scrollBottom();
    },
    appendLast(text) {
      const last = this.lastAssistant();
      if (last) last.text += text;
    },
    lastAssistant() {
      if (!this.messages.length) return null;
      const last = this.messages[this.messages.length - 1];
      return last.role === "assistant" ? last : null;
    },
    finishPending() {
      const last = this.lastAssistant();
      if (last) last.pending = false;
    },
    // ---------------- 会话历史 ----------------
    async loadSessions() {
      this.histLoading = true;
      try {
        const res = await aiSessions(1, 50);
        const body = res && res.data ? res.data : res;
        this.sessions = (body.result && body.result.list) || [];
      } catch (e) {
        this.sessions = [];
      } finally {
        this.histLoading = false;
      }
    },
    async loadSession(id) {
      this.histOpen = false;
      this.sessionId = id;
      try {
        const res = await aiMessages(id);
        const body = res && res.data ? res.data : res;
        const list = (body.result && body.result.list) || body.result || [];
        this.messages = list.map((m) => ({
          role: m.role,
          text: m.content || "",
          cards: normalizeCards(m.cardJson),
          sources: m.sourcesJson || [],
          suggestions: m.suggJson || [],
          tool: m.toolName
            ? { name: m.toolName, label: m.toolLabel || m.toolName, done: true, ms: m.toolMs }
            : null,
          pending: false,
        }));
        this.$nextTick(() => this.scrollBottom());
      } catch (e) {
        this.$message && this.$message.error("加载会话失败");
      }
    },
    async clearSessions() {
      try {
        await aiClearSessions();
        this.sessions = [];
        this.sessionId = null;
        this.messages = [];
        this.histOpen = false;
      } catch (e) {
        this.$message && this.$message.error("清空失败");
      }
    },
    // ---------------- 工具函数 ----------------
    splitPara(text) {
      if (!text) return [];
      return text.split("\n").filter((s) => s.trim() !== "");
    },
    cellText(cell) {
      if (cell == null) return "—";
      if (typeof cell === "object") return cell.v == null ? "—" : String(cell.v);
      return String(cell);
    },
    cellClass(cell) {
      if (cell && typeof cell === "object" && cell.t) return cell.t;
      return "";
    },
    topicTag(tag) {
      return tag || "新会话";
    },
    fmtTime(t) {
      if (!t) return "";
      const d = new Date(t);
      if (isNaN(d.getTime())) return "";
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      const pad = (n) => (n < 10 ? "0" + n : n);
      const hm = pad(d.getHours()) + ":" + pad(d.getMinutes());
      if (sameDay) return "今天 " + hm;
      return pad(d.getMonth() + 1) + "-" + pad(d.getDate());
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.list;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },
};

// 后端落库的 cardJson：单张对象 / 多张数组 / null，统一转成数组
function normalizeCards(cardJson) {
  if (!cardJson) return [];
  if (Array.isArray(cardJson)) return cardJson;
  return [cardJson];
}
</script>

<style scoped>
.ai-assistant {
  position: fixed;
  right: 24px;
  bottom: 24px;
  /* 🔴 z-index 分层契约（2026-10-15）：1500 = 高于全站静态内容（最高 999，大屏装饰层），
     低于所有 Element 浮层（PopupManager 从 2000 起：弹窗/遮罩/message/loading）。
     原值 3000 会压在 el-dialog（默认 2000）之上——弹窗打开时球和呼吸光晕浮在弹窗上，
     遮挡标题与表单。改 1500 后弹窗自然盖住球，无需每个弹窗单独设 z-index。 */
  z-index: 1500;
  font-family: "PingFang SC", "Microsoft YaHei", "Segoe UI", sans-serif;
  /* 浅色简洁 · 环保绿主色 */
  --bg: #eef2f4;
  --surface: #ffffff;
  --surface-2: #f5f8f9;
  --green: #0e9f6e;
  --green-deep: #0b7d5d;
  --green-soft: #e5f4ee;
  --amber: #d97706;
  --amber-soft: #fdf1e0;
  --red: #dc2626;
  --text: #1a2233;
  --muted: #5b6470;
  --faint: #8a93a0;
  --border: #e3e9ed;
  --blue: #2563eb;
  --blue-soft: #eaf1fd;
}
.ai-assistant * {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* 悬浮球
   🔴 设计约束（2026-10-15 重做）：球挂在大屏深蓝底上，原样式是「浅色主题遗留」
   ——纯色 #0e9f6e + 阴影 rgba(26,34,51,.12)，在深底上等于没阴影、入口不吸引人。
   现改为项目亮绿体系（与 .ops-btn-primary 同源）渐变 + 外发光 + 语义图标 + 呼吸光晕。 */
.ball {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 54px;
  height: 54px;
  border-radius: 50%;
  /* 深底上要「发光」才像入口；渐变与主操作按钮统一，避免纯色塑料感 */
  background: linear-gradient(135deg, #00f068 0%, #00b44e 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  user-select: none;
  box-shadow: 0 6px 20px rgba(0, 240, 104, 0.38), 0 0 0 1px rgba(0, 240, 104, 0.5);
  transition: transform 180ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 180ms ease;
  /* 入场：从右下角滑入，让用户注意到新入口 */
  animation: ball-in 520ms cubic-bezier(0.16, 1, 0.3, 1);
}
.ball-ico {
  width: 30px;
  height: 30px;
  transition: transform 180ms ease;
}
.ball:hover {
  transform: scale(1.06);
  box-shadow: 0 10px 28px rgba(0, 240, 104, 0.5), 0 0 0 1px rgba(0, 240, 104, 0.68);
}
/* hover 时图标微放大，进一步强化「可点」 */
.ball:hover .ball-ico {
  transform: scale(1.1);
}
.ball:active {
  transform: scale(0.97);
}
/* 呼吸光晕：替代原来的静态「靶环」描边——大屏上入口容易被忽略，
   渐隐扩散既有存在感又不构成视觉噪音 */
.ball::after {
  content: "";
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 240, 104, 0.45);
  animation: ball-halo 3.2s ease-out infinite;
  pointer-events: none;
}
@keyframes ball-in {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes ball-halo {
  0% {
    transform: scale(0.9);
    opacity: 0.7;
  }
  70%,
  100% {
    transform: scale(1.25);
    opacity: 0;
  }
}
/* 未读提示点：16px → 11px 收敛；描边用球体绿而非面板浅色（浅描边在深底上发脏） */
.ball .dot {
  position: absolute;
  top: 0;
  right: 0;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #ff4d4f;
  border: 2px solid #00c95a;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.22), 0 0 8px rgba(255, 77, 79, 0.65);
  animation: dot-pulse 2.2s ease-in-out infinite;
}
@keyframes dot-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.16);
  }
}
/* 无障碍：系统开启「减少动态效果」时只保留静态样式 */
@media (prefers-reduced-motion: reduce) {
  .ball,
  .ball::after,
  .ball .dot {
    animation: none;
  }
}

/* 面板 */
.panel {
  position: absolute;
  right: 0;
  bottom: 72px;
  width: 432px;
  height: min(628px, calc(100vh - 52px));
  background: var(--surface);
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(26, 34, 51, 0.18);
  border: 1px solid var(--border);
}

/* 头部 */
.phead {
  height: 58px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 0 14px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}
/* 面板头像：与悬浮球同款亮绿渐变，视觉一体（浅色面板内不需要外发光） */
.phead .av {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  background: linear-gradient(135deg, #00d95e 0%, #00a845 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 3px 8px rgba(0, 168, 69, 0.28);
}
.phead .t {
  flex: 1;
  min-width: 0;
}
.phead .t b {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 6px;
}
.phead .t b .live {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--green);
  display: inline-block;
}
.phead .t s {
  display: block;
  font-size: 11px;
  color: var(--faint);
  text-decoration: none;
  margin-top: 2px;
}
.phead .acts {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}
.iconbtn {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 160ms ease;
}
.iconbtn:hover {
  border-color: var(--green);
  color: var(--green-deep);
  background: var(--green-soft);
}
.iconbtn svg {
  width: 16px;
  height: 16px;
}

/* 会话历史侧栏 */
.hist {
  position: absolute;
  top: 58px;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--surface);
  z-index: 45;
  display: flex;
  flex-direction: column;
}
.hist .hh {
  padding: 14px 16px 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.hist .hh a {
  font-size: 12px;
  color: var(--green-deep);
  cursor: pointer;
  font-weight: 500;
}
.hist .hs {
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 12px;
}
.hist-empty {
  color: var(--faint);
  font-size: 12.5px;
  text-align: center;
  padding: 40px 0;
}
.hitem {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 3px;
}
.hitem:hover {
  background: var(--surface-2);
}
.hitem.on {
  background: var(--green-soft);
}
.hitem .ht {
  flex: 1;
  min-width: 0;
}
.hitem .ht b {
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hitem .ht s {
  font-size: 11px;
  color: var(--faint);
  text-decoration: none;
  display: block;
  margin-top: 2px;
}
.hitem .htag {
  font-size: 10.5px;
  color: var(--green-deep);
  background: var(--green-soft);
  padding: 2px 7px;
  border-radius: 10px;
  flex-shrink: 0;
}

/* 消息区 */
.plist {
  flex: 1;
  overflow-y: auto;
  padding: 18px 16px;
  scroll-behavior: smooth;
}
.plist::-webkit-scrollbar {
  width: 5px;
}
.plist::-webkit-scrollbar-thumb {
  background: #d3dce3;
  border-radius: 3px;
}

/* 空态 */
.welcome {
  text-align: center;
  padding: 22px 6px 8px;
}
.welcome .hello {
  width: 62px;
  height: 62px;
  margin: 0 auto 14px;
  border-radius: 19px;
  background: var(--green);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  box-shadow: 0 8px 20px rgba(14, 159, 110, 0.28);
}
.welcome h3 {
  font-size: 17px;
  font-weight: 600;
  color: var(--text);
  margin-bottom: 6px;
}
.welcome p {
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.7;
}
.qs {
  display: grid;
  gap: 8px;
  margin-top: 18px;
  text-align: left;
}
.q {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 13px;
  border: 1px solid var(--border);
  border-radius: 12px;
  cursor: pointer;
  background: var(--surface);
  transition: all 160ms ease;
}
.q:hover {
  border-color: var(--green);
  background: var(--green-soft);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(26, 34, 51, 0.08);
}
.q .qi {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
}
.q .qi.d {
  background: var(--blue-soft);
  color: var(--blue);
}
.q .qi.w {
  background: var(--amber-soft);
  color: var(--amber);
}
.q .qi.t {
  background: var(--green-soft);
  color: var(--green-deep);
}
.q .qi.g {
  background: #f0ecfd;
  color: #7c5ce0;
}
.q .qt b {
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
  display: block;
}
.q .qt s {
  font-size: 11.5px;
  color: var(--faint);
  text-decoration: none;
  display: block;
  margin-top: 1px;
}
.boundary {
  margin-top: 18px;
  padding: 11px 13px;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 11px;
  font-size: 11.5px;
  color: var(--muted);
  line-height: 1.7;
  text-align: left;
}
.boundary b {
  color: var(--green-deep);
  font-weight: 600;
}

/* 消息 */
.msg {
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.msg.user {
  align-items: flex-end;
}
.row {
  display: flex;
  gap: 9px;
  align-items: flex-start;
  width: 100%;
}
.mav {
  width: 28px;
  height: 28px;
  border-radius: 9px;
  background: var(--green);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}
.bub {
  max-width: 82%;
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 13px;
  line-height: 1.7;
  word-break: break-word;
}
.msg.user .bub {
  background: var(--green);
  color: #fff;
  border-bottom-right-radius: 4px;
}
.msg.assistant .bub {
  background: var(--surface-2);
  color: var(--text);
  border: 1px solid var(--border);
  border-bottom-left-radius: 4px;
  flex: 1;
  min-width: 0;
}
.msg.assistant .bub p {
  margin: 7px 0;
}
.msg.assistant .bub p:first-child {
  margin-top: 0;
}
.msg.assistant .bub p:last-child {
  margin-bottom: 0;
}
.caret {
  display: inline-block;
  width: 2px;
  height: 13px;
  background: var(--green);
  vertical-align: -2px;
  animation: blink 0.9s step-end infinite;
}
@keyframes blink {
  50% {
    opacity: 0;
  }
}

/* 工具条 */
.tool {
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 7px 0 9px 37px;
  padding: 8px 11px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-left: 3px solid var(--blue);
  border-radius: 9px;
  font-size: 11.5px;
  color: var(--muted);
}
.tool .spin {
  width: 13px;
  height: 13px;
  border: 2px solid var(--blue);
  border-top-color: transparent;
  border-radius: 50%;
  flex-shrink: 0;
  animation: sp 0.7s linear infinite;
}
.tool.done {
  border-left-color: var(--green);
}
.tool.done .spin {
  border-color: var(--green);
  border-top-color: transparent;
  animation: none;
}
.tool.done .spin::after {
  content: "✓";
  display: block;
  font-size: 9px;
  color: var(--green-deep);
  line-height: 9px;
  text-align: center;
}
.tool b {
  color: var(--text);
  font-weight: 500;
  flex: 1;
  min-width: 0;
}
.tool .ms {
  margin-left: auto;
  font-size: 11px;
  color: var(--faint);
  flex-shrink: 0;
}
@keyframes sp {
  to {
    transform: rotate(360deg);
  }
}

/* 数据卡 */
.dcard {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 11px;
  margin: 9px 0;
  overflow: hidden;
  width: 100%;
}
.dcard .h {
  padding: 8px 12px;
  font-size: 11.5px;
  color: var(--muted);
  border-bottom: 1px solid var(--border);
  background: var(--surface-2);
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: center;
}
.dcard .h b {
  color: var(--text);
  font-weight: 600;
  white-space: nowrap;
}
.dcard .h span {
  white-space: nowrap;
  color: var(--faint);
  font-size: 11px;
}
.dcard table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11.5px;
  table-layout: fixed;
}
.dcard th {
  color: var(--faint);
  font-weight: 500;
  padding: 7px 9px;
  text-align: left;
  border-bottom: 1px solid var(--border);
}
.dcard td {
  padding: 7px 9px;
  color: var(--muted);
  border-bottom: 1px solid #eef2f5;
  word-break: break-all;
}
.dcard tr:last-child td {
  border-bottom: none;
}
.ok {
  color: var(--green-deep);
  font-weight: 600;
}
.warn {
  color: var(--amber);
  font-weight: 600;
}
.bad {
  color: var(--red);
  font-weight: 600;
}

/* 来源 + chips */
.cite {
  margin-top: 8px;
  margin-left: 37px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--faint);
  flex-wrap: wrap;
}
.cite .tag {
  background: var(--green-soft);
  color: var(--green-deep);
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 600;
}
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 9px;
  margin-left: 37px;
}
.chips b {
  font-size: 11.5px;
  color: var(--blue);
  border: 1px solid #c9daf6;
  padding: 4px 11px;
  border-radius: 14px;
  cursor: pointer;
  font-weight: 500;
  background: var(--surface);
  transition: all 160ms ease;
}
.chips b:hover {
  background: var(--blue-soft);
  border-color: var(--blue);
}

/* 输入区 */
.pfoot {
  flex-shrink: 0;
  border-top: 1px solid var(--border);
  background: var(--surface);
  padding: 11px 13px 12px;
}
.qbox {
  display: flex;
  gap: 9px;
  align-items: flex-end;
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 13px;
  padding: 9px 11px;
  transition: border-color 160ms ease;
}
.qbox:focus-within {
  border-color: var(--green);
  box-shadow: 0 0 0 3px rgba(14, 159, 110, 0.12);
}
.qbox .tx {
  flex: 1;
  font-size: 13px;
  color: var(--text);
  min-height: 19px;
  line-height: 1.6;
  outline: none;
  border: none;
  background: transparent;
  resize: none;
  font-family: inherit;
  max-height: 96px;
  overflow-y: auto;
}
.send {
  background: var(--green);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
  font-weight: 600;
  flex-shrink: 0;
  transition: background 160ms ease;
}
.send:hover {
  background: var(--green-deep);
}
.send.off {
  background: #cbd5dc;
  color: #fff;
  cursor: not-allowed;
}
.fnote {
  font-size: 10.5px;
  color: var(--faint);
  margin-top: 7px;
  text-align: center;
}
</style>
